/**
 * Responsible for rendering an error within a floating label.
 * @module elements/fields/floatingError
 */

import $ from 'jquery';
import Symbol from 'es6-symbol';
import { EVENTS, TYPES } from './floating-label';
import { EVENTS as VALIDATOR_EVENTS } from './validator';

const { LOAD, BLUR, FOCUS, INVALID, INPUT, VALIDATE } = EVENTS;
export { LOAD, BLUR, FOCUS, INVALID, INPUT, VALIDATE };

const { CONTAINER, FIELD } = TYPES;
export { CONTAINER };

const { INVALID_SCROLL_POSITION } = VALIDATOR_EVENTS;
export { INVALID_SCROLL_POSITION };

const FLOATING_ERROR_OPTIONS = {
  floatingErrorContainerClassName: 'floating-label--floating-error',
  floatingErrorElement: 'p',
  floatingErrorClassName: '',
};

const KEY = Symbol('error-el');

export const CLASS_NAMES = {
  FLOATING_ERROR: 'floating-error',
};

/**
 * Creates an error element to later on be appended to the DOM.
 * @private
 * @param {string} errorElement - Name of error element to create the element
 * @param {string} className - Optional class name applied when error is present
 * @param {string} message - Message used when an error has occurred
 * @returns {Element} An element containing the floating error
 */
const createErrorEl = (errorElement, className, message) => {
  const el = document.createElement(errorElement);
  el.classList.add(CLASS_NAMES.FLOATING_ERROR);

  if (className) {
    el.classList.add(className);
  }

  el.textContent = message;
  return el;
};

/**
 * Renders an error element into the DOM.
 * @private
 * @param {HTMLElement} container - The floating label element
 * @param {string} errorElement - Name of error element to create the element
 * @param {string} className - Optional class name applied when error is present
 * @param {string} message - Message used when an error has occurred
 * @param {string} position - Position of the error message (top or bottom)
 * @returns {HTMLElement} - The error element
 */
const renderErrorEl = (container, errorElement, className, message, position) => {
  const errorEl = createErrorEl(errorElement, className, message);
  if (position === 'top') {
    container.parentNode.insertBefore(errorEl, container);
  } else {
    container.parentNode.insertBefore(errorEl, container.nextSibling);
  }
  const errorParent = errorEl.previousElementSibling;
  const errorInputEl = errorParent.querySelector('input');
  if (errorInputEl) {
    const ariaVal = `${errorInputEl.getAttribute('id')}2`;
    if (ariaVal) {
      errorEl.setAttribute('id', ariaVal);
      errorInputEl.setAttribute('aria-describedby', ariaVal);
    }
  }
  return errorEl;
};

/**
 * Responsible for removing an error element if it exists.
 * @private
 * @param {HTMLElement} container - The floating label element
 * @param {HTMLElement} errorElement - Element containing the error
 * @param {Map} state - Contains state info around error
 * @param {string} className - className interacting with container
 */
const removeErrorEl = (container, errorElement, state, className) => {
  if (errorElement) {
    container.classList.remove(className);
    errorElement.parentNode.removeChild(errorElement);
    state.delete(KEY);
  }
};

/**
 * Sets up a listener for when an invalid formField has been found so it can
 * set the position of where to scroll to in the DOM.
 * @private
 * @param {HTMLElement} formField - The form field element
 */
const listenForInvalid = (formField) => {
  formField.addEventListener(INVALID_SCROLL_POSITION, ({ detail: { delegateScroll } }) => {
    delegateScroll((highestEl) => {
      highestEl.scrollIntoView({
        block: 'center',
      });
    });
  });
};

/**
 * Responds to any errors that occur on a floating label by providing a message
 * above the floating label.
 * @private
 * @param {string} type - Type of event
 * @param {HTMLElement} target - Element receiving the event
 * @param {HTMLElement} container - The floating label element
 * @param {Map} state - Acts as a state container
 * @param {boolean} validateOnlyOnSubmit - Instructs to only validate on submit
 * @param {string} containerClassName - Class name provided to floating label
 * if an error occurred
 * @param {string} errorElement - Name of error element to create the element
 * @param {string} className - Optional class name applied when error is present
 * @param {string} message - Message used when an error has occurred
 */
const respond = (type, target, container, state, validateOnlyOnSubmit, {
  floatingErrorContainerClassName: containerClassName,
  floatingErrorElement: errorElement,
  floatingErrorClassName: className,
  floatingErrorMessage: message = '',
  floatingErrorPosition: position,

}) => {
  const errorEl = state.get(KEY);
  const errorMessage = message || target.validationMessage;

  switch (type) {
    case LOAD:
    case BLUR:
    case VALIDATE:
      if (!validateOnlyOnSubmit) {
        // Assume there is no error for a clean slate
        removeErrorEl(container, errorEl, state, containerClassName);

        // This may feel redundant since there's a chance the form element
        // may already have an error. However, in the case where the blur
        // event only occurs or where the floating error is the only listener,
        // then this is necessary. Otherwise, it is redundant, but it doesn't
        // hurt anything or have any performance issues to reset the validity.
        if (container.querySelector(':invalid') === target) {
          target.setCustomValidity(errorMessage);
        }

        target.checkValidity();
      }

      break;
    case INPUT:
      removeErrorEl(container, errorEl, state, containerClassName);
      break;
    case FOCUS:
      break;
    case INVALID:
      removeErrorEl(container, errorEl, state, containerClassName);
      // Supercedes the applied validity for one that may be more relevant
      // to the field.
      target.setCustomValidity(errorMessage);
      container.classList.add(containerClassName);
      state.set(KEY, renderErrorEl(container, errorElement, className, errorMessage, position));
      break;
    default:
      break;
  }
};

/**
 * Responsible for rendering an error message when an error occurs on a floating
 * label. To avoid any weirdness with CSS positioning, the error message appears
 * outside of the floating label and is referred to as a floating error.
 *
 * In order to activate this functionality, the floating label must contain the
 * following data attribute:
 *
 * - data-floating-error-message
 *
 * Other options include:
 *
 * - `data-floating-error-element`: The name of the element used for the floating
 *   error. Defaults to a `p` (for paragraph).
 * - `data-floating-error-class-name`: An additional className added to the
 *   floating error message element. Defaults to `''`.
 * - `data-floating-container-class-name`: A className applied to the floating
 *   label container when the floating error is shown. Defaults to
 *   `floating-label--floating-error`.
 *
 * @example
 * <div class="floating-label" data-floating-error-message="Hey dummy!">
 *  <label for="dude" class="floating-label__label">Something</label>
 *  <input
 *    required
 *    id="dude"
 *    type="text"
 *    class="floating-label__input"
 *    placeholder="Enter something">
 * </div>
 * @see FLOATING_ERROR_OPTIONS
 * @param {Object} options - Configurable options
 * @returns {Function} Used to setup and respond to events from floating label
 */
const floatingError = (options = {}) => (...nodes) => {
  const [{ node: container }] = nodes.filter(({ type }) => type === CONTAINER);
  const [{ node: formField }] = nodes.filter(({ type }) => type === FIELD);
  const opts = $.extend({}, FLOATING_ERROR_OPTIONS, options, $(container).data());
  const state = new Map();

  listenForInvalid(formField, state);
  return (type, { target }, { validateOnlyOnSubmit }) =>
    respond(type, target, container, state, validateOnlyOnSubmit, opts);
};

export default floatingError;



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/floating-error.js